/**
 * KAN-852: блок "Контакты и данные" карточки клиента в приложении менеджера:
 * телефон, email, источник. KAN-856: Telegram и наш аккаунт переписки
 * переехали в шапку (MobileClientTelegram). Длинное (досье, что выяснил бот, контакты ЛПР) под "Ещё":
 * карточки кабинета LeadAttributesCard и LeadContactsCard.
 * KAN-889: контакты ЛПР правятся ("Изменить"); без контактов "Добавить ЛПР" прямо в блоке.
 */
import { useState, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { LeadAttributesCard } from '@/components/clients/LeadAttributesCard';
import { LeadContactsCard } from '@/components/clients/TelegramContacts';
import { useCanCallApi } from '@/hooks/useApiAccess';
import { cn } from '@/lib/utils';
import type { Client } from '@/types/client';

const LINK = 'break-all text-blue-600 underline-offset-2 active:underline';

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex min-h-[44px] items-center justify-between gap-3 py-1.5 text-sm">
      <dt className="shrink-0 text-gray-500">{label}</dt>
      <dd className="min-w-0 text-right text-gray-900">{children}</dd>
    </div>
  );
}

function hasMore(client: Client): boolean {
  return (
    !!client.summary?.trim() ||
    (client.lead_attributes?.length ?? 0) > 0 ||
    (client.lead_contacts?.length ?? 0) > 0
  );
}

export function MobileClientContacts({ client }: { client: Client }): JSX.Element {
  const [open, setOpen] = useState(false);
  const more = hasMore(client);
  // KAN-889: the decision-maker contact is edited by the card PATCH.
  const canEdit = useCanCallApi('PATCH', `/api/v1/clients/${client.id}/`);
  const hasLeadContacts = (client.lead_contacts?.length ?? 0) > 0;

  return (
    <section className="rounded-xl border border-gray-200 bg-white p-4" data-testid="mobile-client-contacts">
      <h2 className="text-base font-semibold text-gray-900">Контакты и данные</h2>
      <dl className="mt-1 divide-y divide-gray-100">
        {client.phone && (
          <Row label="Телефон">
            <a href={`tel:${client.phone}`} className={LINK} data-testid="mobile-client-phone">
              {client.phone}
            </a>
          </Row>
        )}
        {client.email && (
          <Row label="Email">
            <a href={`mailto:${client.email}`} className={LINK} data-testid="mobile-client-email">
              {client.email}
            </a>
          </Row>
        )}
        {client.source_label && <Row label="Источник">{client.source_label}</Row>}
      </dl>

      {/* KAN-889: no contacts yet: "Добавить ЛПР" right in the block, not under "Ещё". */}
      {!hasLeadContacts && canEdit && (
        <div className="mt-2">
          <LeadContactsCard contacts={client.lead_contacts} clientId={client.id} canEdit />
        </div>
      )}

      {more && (
        <>
          <button
            type="button"
            onClick={() => setOpen(!open)}
            aria-expanded={open}
            className="mt-2 flex min-h-[44px] w-full items-center justify-center gap-1 rounded-lg border border-gray-200 text-sm font-medium text-gray-700"
            data-testid="mobile-client-more"
          >
            {open ? 'Свернуть' : 'Ещё'}
            <ChevronDown className={cn('h-4 w-4 transition-transform', open && 'rotate-180')} aria-hidden="true" />
          </button>
          {open && (
            <div className="mt-3 space-y-3" data-testid="mobile-client-more-body">
              {client.summary?.trim() && (
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Досье</h3>
                  <p className="mt-1 whitespace-pre-line text-sm text-gray-900" data-testid="mobile-client-dossier">
                    {client.summary}
                  </p>
                </div>
              )}
              <LeadAttributesCard attributes={client.lead_attributes} />
              <LeadContactsCard contacts={client.lead_contacts} clientId={client.id} canEdit={canEdit && hasLeadContacts} />
            </div>
          )}
        </>
      )}
    </section>
  );
}
